
import { IconAlertTriangleFilled, IconPlus, IconTrash } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import {
    NSTable,
    NSCellInput,
} from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { BedMix } from 'views/ProFormaTable/types';
import {
    OPERATIONS_INCOME_WS_HANDLERS, CASH_FLOW_TOOLTIP_MESSAGE,
    RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_OPERATIONS_TEXT,
} from 'views/ProFormaTable/constants';
import { doesRentableSFExceedBuiltGSF } from 'views/ProFormaTable/helpers';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import './BedMixTable.scss';

export interface IBedMixTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const BedMixTable = ({ operationBeginIndex, baseCashFlowColumns }: IBedMixTableProps) => {
    const {
        incomeSummary, handleCreateOperationsRow, handleUpdateOperationsRow, handleDeleteOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow, proForma,
    } = useProFormaSocketContext();

    const handleUpdateBedMix = (id: number, field: string) => handleUpdateOperationsRow(
        OPERATIONS_INCOME_WS_HANDLERS.BED_MIX.UPDATE, id, field,
    );

    const shouldShowRSFWarning = doesRentableSFExceedBuiltGSF(
        Number(proForma.builtGrossSquareFootage), incomeSummary.totalRentableSf,
    );

    const bedMixCashFlowColumns: ColumnDef<BedMix>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `bed-mix-period-${column.id}`,
        accessorFn: (row, bedMixIndex: number) => (
            typeof operationsCashFlow?.[operationBeginIndex]?.beds?.[bedMixIndex]?.value === 'number'
                ? (operationsCashFlow?.[cashFlowIndex]?.beds?.[bedMixIndex]?.value || 0)
                : null
        ),
        footer: () => (
            <span className="text-dark">
                {operationBeginIndex
                    ? formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.grossRent || 0, showCents, 'currency')
                    : ' '}
            </span>
        ),
    }));

    const bedMixColumns: ColumnDef<BedMix>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            header: () => (
                <div className="d-flex align-items-center justify-content-between w-100 px-1">
                    <span>Bed mix</span>
                    <NSIcon
                        icon={IconPlus}
                        className="ml-2 text-muted cursor--pointer"
                        aria-label="Add bed mix row"
                        onClick={() => handleCreateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.BED_MIX.CREATE)}
                    />
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="d-flex align-items-center justify-content-between w-100 pr-1">
                    <div className="w-100">
                        <NSCellInput
                            type="text"
                            value={getValue()}
                            handleUpdateValue={updatedValue => {
                                const updateFn = handleUpdateBedMix(Number(row.original.id), 'name');
                                if (typeof updateFn === 'function') {
                                    updateFn({
                                        target: { value: updatedValue },
                                    } as React.ChangeEvent<HTMLInputElement>);
                                }
                            }}
                            placeholder="Enter name"
                        />
                    </div>
                    <NSIcon
                        icon={IconTrash}
                        className="ml-1 text-dark cursor--pointer"
                        aria-label={`Delete ${row.original.name || 'bed mix row'}`}
                        onClick={() => handleDeleteOperationsRow(
                            OPERATIONS_INCOME_WS_HANDLERS.BED_MIX.DELETE, Number(row.original.id),
                        )}
                    />
                </div>
            ),
            footer: () => <span className="px-1">Total bed mix income</span>,
        },
        {
            accessorKey: 'units',
            id: 'units',
            header: () => <span># of Units</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateBedMix(Number(row.original.id), 'units');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter value"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.grossRent?.totalUnits || 0, false)}</span>,
        },
        {
            accessorKey: 'averageSf',
            id: 'averageSf',
            header: () => (
                <div className={classNames({ 'd-flex': shouldShowRSFWarning })}>
                    <RenderIf isTrue={shouldShowRSFWarning}>
                        <IconWithTooltip
                            name="RSFDiscrepancyIcon-bed-mix"
                            icon={IconAlertTriangleFilled}
                            tooltipText={RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_OPERATIONS_TEXT}
                            iconClassName="text-warning"
                            className="my-auto mx-1"
                        />
                    </RenderIf>
                    <span>Average RSF</span>
                </div>
            ),
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateBedMix(Number(row.original.id), 'averageSf');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter value"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.grossRent?.averageSf || 0, false)}</span>,
        },
        {
            accessorKey: 'bedsPerUnit',
            id: 'bedsPerUnit',
            header: () => <span>Beds/unit</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateBedMix(Number(row.original.id), 'bedsPerUnit');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter value"
                />
            ),
            footer: () => null,
        },
        {
            accessorKey: 'totalBeds',
            id: 'totalBeds',
            header: () => <span>Total beds</span>,
            cell: ({ getValue }) => (
                <span className="d-block text-right text-dark">{formatDisplayValue(getValue() || 0, false)}</span>
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.grossRent?.totalBeds || 0, false)}</span>,
        },
        {
            accessorKey: 'amountOfRentPerBed',
            id: 'amountOfRentPerBed',
            header: () => <span>Rent/bed (Mo.)</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateBedMix(Number(row.original.id), 'amountOfRentPerBed');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter value"
                />
            ),
            footer: () => (
                <span>{formatDisplayValue(incomeSummary.grossRent?.pricePerBed || 0, true, 'currency')}</span>
            ),
        },
        {
            accessorKey: 'amountPerYear',
            id: 'amountPerYear',
            header: () => <span className="pr-1">Amount/year</span>,
            cell: ({ getValue }) => (
                <span className="d-block text-right text-dark pr-1">
                    {formatDisplayValue(getValue() || 0, showCents, 'currency')}
                </span>
            ),
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(incomeSummary.grossRent?.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...bedMixCashFlowColumns,
    ];

    const table = useReactTable({
        data: incomeSummary.bedMix || [],
        columns: bedMixColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className="NSTable BedMixTable mb-1">
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                <RenderIf isTrue={table.getRowModel().rows.length}>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </RenderIf>
                <RenderIf isTrue={!table.getRowModel().rows.length}>
                    <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        <td colSpan={7} className="NSTable__tbody__tr__td text-center text-muted py-4">
                            <div className="text-dark">Your added bed types will appear here.</div>
                            <div className="small">
                                Click the
                                {' '}
                                <span className="font-weight-bold text-dark">[+]</span>
                                {' '}
                                symbol in the
                                {' '}
                                <span className="font-weight-bold text-dark">Bed mix</span>
                                {' '}
                                header to add your first bed type.
                            </div>
                        </td>
                        {baseCashFlowColumns.map((column, index) => (
                            <td
                                key={`bed-mix-period-${column.id}`}
                                className="NSTable__tbody__tr__td"
                                id={`bed-mix-placeholder-period-${index}`}
                            >
                                <RenderIf isTrue={baseCashFlowColumns.length === 1 && index === 0}>
                                    <UncontrolledTooltip target={`bed-mix-placeholder-period-${index}`}>
                                        {CASH_FLOW_TOOLTIP_MESSAGE}
                                    </UncontrolledTooltip>
                                </RenderIf>
                            </td>
                        ))}
                    </tr>
                </RenderIf>
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default BedMixTable;
